import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, User, Mail, Facebook, Instagram, Loader2 } from "lucide-react";
import { FadeIn } from "./FadeIn";
import {
  CONTACT_PERSON,
  CONTACT_MOBILE,
  CONTACT_MOBILE_DISPLAY,
  BOOKING_EMAIL,
  FACEBOOK_URL,
  INSTAGRAM_URL,
} from "@/config/siteConfig";

const API = `/.netlify/functions`;

export const Contact = () => {
  const [form, setForm] = useState({ full_name: "", event_date: "", guest_count: "", contact_number: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    
    // console.log("Submit clicked");
    setLoading(true);

    try {
      // console.log("Before axios");

      await axios.post(`${API}/send-enquiry`, form);
      // console.log("Axios completed");
      // console.log(response);
      
      toast.success("Enquiry sent! Our team will contact you shortly.");
      setForm({ full_name: "", event_date: "", guest_count: "", contact_number: "" });

      // console.log("Form reset");
    } catch {
      // console.error("Axios Error:", err);
      toast.error("Something went wrong. Please call us directly.");
    } finally {
      // console.log("Finally block");
      setLoading(false);
      // console.log("Loading false");
    }
  };

  const inputCls =
    "w-full bg-white border border-[#043927]/25 rounded-sm px-5 py-3.5 text-sm text-[#1A1A1A] placeholder:text-[#1A1A1A]/40 focus:outline-none focus:ring-2 focus:ring-[#D4AF37] transition-shadow duration-300";

  return (
    <section id="contact" data-testid="contact-section" className="grain-overlay bg-[#FAF9F6] py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
        <div>
          <FadeIn>
            <p className="text-[#097969] uppercase tracking-[0.4em] text-xs mb-5">Direct Conversions</p>
            <h2 className="font-serif-luxe text-4xl md:text-5xl font-medium text-[#043927] leading-tight">
              Contact & <span className="italic">Booking Enquiry</span>
            </h2>
            <p className="mt-6 text-[#1A1A1A]/70 leading-relaxed font-light max-w-md">
              Share a few details and our team will curate the perfect celebration for you. Enquiries reach us instantly at {BOOKING_EMAIL}.
            </p>
          </FadeIn>

          <FadeIn delay={0.15}>
            <div className="mt-12 space-y-6">
              <div className="flex items-center gap-5">
                <div className="h-12 w-12 rounded-full border border-[#D4AF37]/50 flex items-center justify-center">
                  <User className="text-[#D4AF37]" size={20} strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.25em] text-[#097969]">Contact Person</p>
                  <p data-testid="contact-person-name" className="font-serif-luxe text-xl text-[#043927] mt-0.5">{CONTACT_PERSON}</p>
                </div>
              </div>
              <div className="flex items-center gap-5">
                <div className="h-12 w-12 rounded-full border border-[#D4AF37]/50 flex items-center justify-center">
                  <Phone className="text-[#D4AF37]" size={20} strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.25em] text-[#097969]">Mobile Number</p>
                  <a data-testid="contact-phone-link" href={`tel:${CONTACT_MOBILE}`} className="font-serif-luxe text-xl text-[#043927] mt-0.5 hover:text-[#097969] transition-colors duration-300 block">
                    {CONTACT_MOBILE_DISPLAY}
                  </a>
                </div>
              </div>
              <div className="flex items-center gap-5">
                <div className="h-12 w-12 rounded-full border border-[#D4AF37]/50 flex items-center justify-center">
                  <Mail className="text-[#D4AF37]" size={20} strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.25em] text-[#097969]">Booking Email</p>
                  <a data-testid="contact-email-link" href={`mailto:${BOOKING_EMAIL}`} className="font-serif-luxe text-xl text-[#043927] mt-0.5 hover:text-[#097969] transition-colors duration-300 block">
                    {BOOKING_EMAIL}
                  </a>
                </div>
              </div>
            </div>

            <div className="mt-12 flex items-center gap-5">
              {/* <!-- INSERT FACEBOOK URL HERE (configured in siteConfig.js) --> */}
              <a href={FACEBOOK_URL} target="_blank" rel="noopener noreferrer" data-testid="social-facebook-link" aria-label="Facebook"
                className="h-11 w-11 rounded-full border border-[#043927]/30 flex items-center justify-center text-[#043927] hover:bg-[#043927] hover:text-[#D4AF37] transition-colors duration-300">
                <Facebook size={18} strokeWidth={1.5} />
              </a>
              {/* <!-- INSERT INSTAGRAM URL HERE (configured in siteConfig.js) --> */}
              <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" data-testid="social-instagram-link" aria-label="Instagram"
                className="h-11 w-11 rounded-full border border-[#043927]/30 flex items-center justify-center text-[#043927] hover:bg-[#043927] hover:text-[#D4AF37] transition-colors duration-300">
                <Instagram size={18} strokeWidth={1.5} />
              </a>
            </div>
          </FadeIn>
        </div>

        <FadeIn delay={0.2}>
          <form onSubmit={submit} data-testid="enquiry-form" className="bg-white border border-[#D4AF37]/30 rounded-sm p-8 md:p-12 shadow-[0_8px_32px_rgba(4,57,39,0.08)]">
            <h3 className="font-serif-luxe text-3xl text-[#043927] mb-8">Plan Your Event</h3>
            <div className="space-y-5">
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-[#097969] mb-2">Full Name</label>
                <input required data-testid="enquiry-input-name" value={form.full_name} onChange={set("full_name")} placeholder="Your full name" className={inputCls} />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-[#097969] mb-2">Event Date</label>
                <input required data-testid="enquiry-input-date" type="date" value={form.event_date} onChange={set("event_date")} className={inputCls} />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-[#097969] mb-2">Estimated Guest Count</label>
                <input required data-testid="enquiry-input-guests" type="number" min="1" value={form.guest_count} onChange={set("guest_count")} placeholder="e.g. 350" className={inputCls} />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-[#097969] mb-2">Contact Number</label>
                <input required data-testid="enquiry-input-phone" type="tel" pattern="[+0-9 ]{10,15}" value={form.contact_number} onChange={set("contact_number")} placeholder="+91 XXXXX XXXXX" className={inputCls} />
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="enquiry-submit-btn"
              className="gold-btn w-full mt-9 py-4 rounded-sm text-sm font-semibold tracking-[0.2em] uppercase disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {loading && <Loader2 className="animate-spin" size={16} />}
              {loading ? "Sending..." : "Send Enquiry"}
            </button>
          </form>
        </FadeIn>
      </div>
    </section>
  );
};
